import React from 'react';
import { SubscriptionData, SubscriptionPackage } from '../BillingTypes';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useSettings } from 'services/settings/SettingsContext';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { formatDate } from 'services/utils/dateTimeUtils';
import {
    calculateCurrentSubscriptionAmount,
    countActiveNormalSeats,
    countActiveFieldSeats,
    countActiveLightSeats,
    getCurrentSubscriptionSeatPrice,
    formatPriceOnUserLocale
} from '../functions/billingUtils';

interface SubscriptionDetailsCardProps {
    loading: string;
    packages: SubscriptionPackage[];
    subscription: SubscriptionData;
    subscriptionType: 'current' | 'pending';
}

const SubscriptionDetailsCard: React.FC<SubscriptionDetailsCardProps> = ({
    loading, packages, subscription, subscriptionType
}) => {
    const { t } = useTranslation();
    const history = useHistory();
    const { userLocale } = useSettings();

    // The view only package never allows a subscription change, also not for superusers
    const { packageIsViewOnly } = useAuthContext();

    // Find the selected package
    const selectedPackageObject = packages.find(pkg => pkg.key === subscription.package);

    // Get the active additional modules of this subscription
    const getAdditionalModules = () => {
        return subscription.subscription_lines?.filter(line => line.is_active && line.type === 'additional_module') || [];
    };

    // Get the active discount lines of this subscription
    const getDiscountLines = () => {
        return subscription.subscription_lines?.filter(line => line.is_active && line.type === 'discount') || [];
    };

    // Calculate the monthly recurring price
    const calculateMonthlyRecurringPrice = () => {
        if (subscription.subscription_lines) {
            const calculatedPrice = calculateCurrentSubscriptionAmount({
                subscription_lines: subscription.subscription_lines, 
                displayPrice: 'monthly',
                returnAmount: 'totalExclVat',
                userLocale,
                useDisplayPrice: true
            })

            return calculatedPrice;
        }
    };

    return (
        <div className={`card subscription-details ${subscriptionType === 'pending' && 'pending-subscription'}`}>
            <div className='subscription-details-header'>
                <h6>
                    {subscriptionType === 'current' && (
                        subscription.is_trial ? (
                            t('billing.general.current_trial_subscription_title')
                        ) : (
                            t('billing.general.current_subscription_title')
                        )
                    )}
                    {subscriptionType === 'pending' && t('billing.general.pending_subscription_short_title')}
                </h6>
                {subscription.start_date && subscription.end_date && (
                    <p>{formatDate(subscription.start_date, userLocale)} - {formatDate(subscription.end_date, userLocale)}</p>
                )}
            </div>
            {loading === 'show-loader' ? (
                <div className="loader"></div>
            ) : loading === 'success' && (
                <div className='subscription-details-content'>
                    <div>
                        <div>
                            <div>
                                {selectedPackageObject && (
                                    <p>
                                        {subscription.is_trial ? (
                                            t('billing.general.current_trial_package_label', { currentPackage: selectedPackageObject.name })
                                        ) : (
                                            t('billing.general.current_package_label', { currentPackage: selectedPackageObject.name })
                                        )}
                                    </p>
                                )}
                                <p>
                                    {t('billing.general.current_normal_seats_and_price_label', { 
                                        normalSeats: countActiveNormalSeats(subscription.subscription_lines), 
                                        currentNormalSeatPrice: getCurrentSubscriptionSeatPrice('normal_seats', userLocale, subscription.subscription_lines) 
                                    })}
                                </p>
                                {countActiveFieldSeats(subscription.subscription_lines) > 0 && (
                                    <p>
                                        {t('billing.general.current_field_seats_and_price_label', { 
                                            fieldSeats: countActiveFieldSeats(subscription.subscription_lines), 
                                            currentFieldSeatPrice: getCurrentSubscriptionSeatPrice('field_seats', userLocale, subscription.subscription_lines) 
                                        })}
                                    </p>
                                )}
                                {countActiveLightSeats(subscription.subscription_lines) > 0 && (
                                    <p>
                                        {t('billing.general.current_light_seats_and_price_label', { 
                                            lightSeats: countActiveLightSeats(subscription.subscription_lines), 
                                            currentLightSeatPrice: getCurrentSubscriptionSeatPrice('light_seats', userLocale, subscription.subscription_lines) 
                                        })}
                                    </p>
                                )}
                                {!subscription.is_trial && (subscription.duration === 'yearly' || subscription.duration === 'monthly') && (
                                    <p>{t(`billing.general.${subscription.duration}_payment_label`)}</p>
                                )}
                            </div>

                            {/* Additional modules */}
                            {getAdditionalModules().length > 0 && (
                                <div className='module-details'>
                                    <h6>{t('billing.general.additional_modules_label')}</h6>
                                    {getAdditionalModules().map((line, index) => (
                                        <p key={index}>
                                            {t('billing.general.additional_module_line_label', {
                                                productName: t(`billing.feature.${line.product_name}`, { defaultValue: line.product_name || '' }),
                                                modulePrice: formatPriceOnUserLocale(parseFloat(line.price), userLocale)
                                            })}
                                        </p>
                                    ))}
                                </div>
                            )}
                            
                            {/* Discounts */}
                            {getDiscountLines().length > 0 && (
                                <div className='module-details'>
                                    <h6>{t('billing.general.discount_label')}</h6>
                                    {getDiscountLines().map((line, index) => (
                                        <p key={index}>
                                            {t('billing.general.discount_line_label', { productName: line.product_name, discountPercentage: line.price })}
                                        </p>
                                    ))}
                                </div>
                            )}
                        </div>
                        {subscriptionType === 'current' && subscription.status !== 'cancelled' && !subscription.is_provisional && !packageIsViewOnly && (
                            <div className='text-button'
                                 onClick={() => history.push(`subscription/wizard/1`)}>
                                {subscription.is_trial ? (
                                    t('billing.general.configure_subscription_label')
                                ) : (
                                    t('billing.general.edit_subscription_label')
                                )}
                            </div>
                        )}
                    </div>
                    {subscription.subscription_lines && (
                        <div className='monthly-price'>
                            <div className='price-big'>
                                € {calculateMonthlyRecurringPrice()}
                            </div>
                            <p>{t(`billing.general.per_month_label`)}</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

export default SubscriptionDetailsCard;